/**
 * Pure file/route link helpers — NO firebase, NO network (2026-08-03 refactor).
 * These drive the document-level link interceptor and the 3D route intercept,
 * so they must be importable from any surface (and any test) without dragging
 * the auth SDK in. resolve3d.ts re-exports them for existing callers.
 */

/** Viewer route prefix — lockstep with App.tsx, the chat sanitizer whitelist, and
 *  functions/src/prest0nSession/threeDPreviewDeliverable.ts. */
export const THREE_D_PREVIEW_ROUTE_PREFIX = '/preview/3d/';

/** Artifact id from a viewer route ('/preview/3d/t3d-x-0' → 't3d-x-0'), or null. */
export function threeDArtifactIdFromRoute(value: string | undefined | null): string | null {
  const v = String(value || '').trim();
  if (!v.toLowerCase().startsWith(THREE_D_PREVIEW_ROUTE_PREFIX)) return null;
  const id = v.slice(THREE_D_PREVIEW_ROUTE_PREFIX.length).replace(/\/+$/, '');
  return /^t3d-[a-z0-9-]{1,96}$/.test(id) ? id : null;
}

const SCENE_URL_RE = /^https:\/\/[^\s"'<>]+\.(?:3d|scene)\.html?$/i;
const SCENE_PATH_RE = /\.(3d|scene)\.html?$/i;
const PREVIEWABLE_FILE_PATH_RE = /\.(pdf|docx|xlsx|xls|csv|pptx|png|jpe?g|gif|webp|svg|mp4|webm|mov|mp3|wav|m4a|flac|txt|md|json)$/i;
// F3 universal preview (2026-08-03, owner directive): ANY platform-hosted
// file link opens the in-app previewer (PreviewBody's binary kind renders a
// graceful download card for unknown types) instead of navigating away.
// Page-ish extensions stay ordinary links (a .html link is a webpage; the
// 3D scene naming contract is handled by SCENE_PATH_RE above).
const ANY_FILE_PATH_RE = /\.[A-Za-z0-9]{1,8}$/;
const PAGE_PATH_DENY_RE = /\.(html?|php|aspx?|jspx?|cgi)$/i;

export interface LinkedFileAttachment {
  name: string;
  type: string;
  storageUrl: string;
}

/** Raw 3D scene URL → previewable attachment shape (or null). */
export function sceneAttachmentFromUrl(href: string | null | undefined): LinkedFileAttachment | null {
  const url = String(href || '').trim();
  if (!SCENE_URL_RE.test(url)) return null;
  let name = 'model.3d.html';
  try {
    const last = new URL(url).pathname.split('/').filter(Boolean).pop() || '';
    if (SCENE_PATH_RE.test(last)) name = decodeURIComponent(last);
  } catch {
    return null;
  }
  return { name, type: 'text/html', storageUrl: url };
}

/**
 * Hosts the PLATFORM itself serves files from. Mirrors the server-side fence in
 * functions/src/prest0nSession/fileLinkRescue.ts: sslip.io hosts must be public
 * dashed-IPv4 VM addresses, never private/loopback/metadata ranges.
 */
function isPublicDashedIpSslipHost(host: string): boolean {
  const m = /^(?:.*\.)?(\d{1,3})-(\d{1,3})-(\d{1,3})-(\d{1,3})\.sslip\.io$/.exec(host);
  if (!m) return false;
  const octets = m.slice(1, 5).map(Number);
  if (octets.some((o) => !Number.isInteger(o) || o < 0 || o > 255)) return false;
  const [a, b] = octets;
  if (a === 10 || a === 127 || a === 0) return false;
  if (a === 172 && b >= 16 && b <= 31) return false;
  if (a === 192 && b === 168) return false;
  if (a === 169 && b === 254) return false;
  if (a === 100 && b >= 64 && b <= 127) return false;
  return true;
}

function isPlatformFileHost(host: string): boolean {
  const h = host.toLowerCase();
  return (
    h === 'storage.googleapis.com' ||
    h.endsWith('.storage.googleapis.com') ||
    h === 'firebasestorage.googleapis.com' ||
    isPublicDashedIpSslipHost(h)
  );
}

/**
 * Should a link click open the in-platform previewer instead of navigating?
 * Pure so every branch is testable — jsdom cannot mint trusted events, so the
 * DOM suite can only exercise the untrusted paths (review 2026-08-03).
 *
 * NEVER intercept: programmatic clicks (the previewer's own download helper
 * appends `<a download>` and calls .click() — intercepting that killed every
 * Download button), explicit download links, modified/middle clicks, and any
 * link that isn't a platform-hosted previewable file.
 */
export interface LinkClickIntent {
  isTrusted: boolean;
  button: number;
  hasModifier: boolean;
  defaultPrevented: boolean;
  hasDownloadAttribute: boolean;
  href: string | null;
}

export function shouldInterceptLinkClick(intent: LinkClickIntent): boolean {
  if (intent.defaultPrevented) return false;
  if (!intent.isTrusted) return false;
  if (intent.button !== 0) return false;
  if (intent.hasModifier) return false;
  if (intent.hasDownloadAttribute) return false;
  return fileAttachmentFromUrl(intent.href) !== null;
}

/**
 * PURE. Is this last path segment a file's name? Any extension but a web page's
 * (F3 universal preview: an unknown type lands on the previewer's download
 * card), and a 3D scene's `.3d.html`. Read by fileAttachmentFromUrl and by the
 * link door (src/lib/linkLanding.ts), which leaves a file to the browser or the
 * previewer and never opens it as a page.
 */
export function isFileName(last: string): boolean {
  return PREVIEWABLE_FILE_PATH_RE.test(last) || SCENE_PATH_RE.test(last)
    || (ANY_FILE_PATH_RE.test(last) && !PAGE_PATH_DENY_RE.test(last));
}

/**
 * A platform-hosted previewable file link → attachment shape the previewer can
 * open (2026-08-03: an xlsx + pdf delivered as raw chat links opened a new tab
 * instead of the in-platform preview). Ordinary web links return null and keep
 * their normal navigation.
 */
export function fileAttachmentFromUrl(href: string | null | undefined): LinkedFileAttachment | null {
  const url = String(href || '').trim();
  if (!/^https:\/\//i.test(url)) return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.username || parsed.password) return null;
  if (!isPlatformFileHost(parsed.hostname)) return null;
  const last = parsed.pathname.split('/').filter(Boolean).pop() || '';
  if (!isFileName(last)) return null;
  let name: string;
  try {
    name = decodeURIComponent(last);
  } catch {
    name = last;
  }
  // Type left empty: previewKind falls back to the extension for untyped files.
  return { name, type: SCENE_PATH_RE.test(last) ? 'text/html' : '', storageUrl: url };
}
